import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  Input,
  Textarea,
  Select,
  Option,
  Table,
  Sheet,
  IconButton,
  Alert,
  Divider,
  Grid,
  FormControl,
  FormLabel,
  Chip,
} from '@mui/joy';
import {
  Plus as Add,
  Trash as Delete,
  FloppyDisk as Save,
  PaperPlaneTilt as Send,
  ArrowLeft as ArrowBack,
  Warning,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../lib/apiAccounting';
import type { 
  ChartOfAccount, 
  JournalEntry, 
  JournalEntryFormData 
} from '../../types/accounting';

interface JournalLine {
  account: number | null;
  description: string;
  debit: string;
  credit: string;
  job?: number | null;
  department?: string;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

export const JournalEntryForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = location.pathname.includes('/edit');
  const isView = Boolean(id && !isEdit);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);

  // Form fields
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [memo, setMemo] = useState('');
  const [entryType, setEntryType] = useState<'STANDARD' | 'ADJUSTING' | 'REVERSING'>('STANDARD');
  const [lines, setLines] = useState<JournalLine[]>([
    { account: null, description: '', debit: '', credit: '', job: null, department: '' },
    { account: null, description: '', debit: '', credit: '', job: null, department: '' },
  ]);

  // Fetch accounts for dropdown
  useEffect(() => {
    fetchAccounts();
    if (id) {
      fetchEntry();
    }
  }, [id]);

  const fetchAccounts = async () => {
    try {
      const response = await apiAccountingClient.getChartOfAccounts({
        is_active: true,
        page_size: 1000,
      });
      setAccounts(response.results);
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
      setError('Failed to load chart of accounts');
    }
  };

  const fetchEntry = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const entry = await apiAccountingClient.getJournalEntry(parseInt(id));
      
      // Set form fields from fetched entry
      setEntryDate(entry.entry_date);
      setReferenceNumber(entry.reference_number || '');
      setMemo(entry.memo || '');
      setEntryType(entry.entry_type as any);
      
      // Convert entry lines to form format
      if (entry.lines && entry.lines.length > 0) {
        setLines(entry.lines.map(line => ({
          account: line.account,
          description: line.description || '',
          debit: line.debit_amount ? line.debit_amount.toString() : '',
          credit: line.credit_amount ? line.credit_amount.toString() : '',
          job: line.job || null,
          department: line.department || '',
        })));
      }
    } catch (err) {
      console.error('Failed to fetch journal entry:', err);
      setError('Failed to load journal entry');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const { totalDebits, totalCredits, isBalanced } = useMemo(() => {
    const debits = lines.reduce((sum, line) => {
      const amount = parseFloat(line.debit) || 0;
      return sum + amount;
    }, 0);

    const credits = lines.reduce((sum, line) => {
      const amount = parseFloat(line.credit) || 0;
      return sum + amount;
    }, 0);

    return {
      totalDebits: debits,
      totalCredits: credits,
      isBalanced: Math.abs(debits - credits) < 0.01,
    };
  }, [lines]);

  const handleLineChange = (index: number, field: keyof JournalLine, value: any) => {
    const updatedLines = [...lines];
    updatedLines[index] = {
      ...updatedLines[index],
      [field]: value,
    };
    setLines(updatedLines);
  };

  const addLine = () => {
    setLines([...lines, { 
      account: null, 
      description: '', 
      debit: '', 
      credit: '', 
      job: null, 
      department: '' 
    }]);
  };

  const removeLine = (index: number) => {
    if (lines.length > 2) {
      setLines(lines.filter((_, i) => i !== index));
    }
  };

  const validateEntry = (): boolean => {
    // Check if balanced
    if (!isBalanced) {
      setError('Journal entry must be balanced (debits must equal credits)');
      return false;
    }

    // Check if at least 2 lines with accounts
    const validLines = lines.filter(line => line.account && (line.debit || line.credit));
    if (validLines.length < 2) {
      setError('Journal entry must have at least 2 lines with accounts and amounts');
      return false;
    }

    // Check each line has either debit or credit, not both
    for (const line of lines) {
      if (line.debit && line.credit) {
        setError('Each line can have either a debit or credit amount, not both');
        return false;
      }
    }

    return true;
  };

  const handleSave = async (shouldPost = false) => {
    setError(null);
    
    if (!validateEntry()) {
      return;
    }

    setSaving(true);
    try {
      // Prepare data
      const formData: JournalEntryFormData = {
        entry_date: entryDate,
        reference_number: referenceNumber || undefined,
        memo: memo || undefined,
        entry_type: entryType,
        lines: lines
          .filter(line => line.account && (line.debit || line.credit))
          .map(line => ({
            account: line.account!,
            description: line.description || undefined,
            debit_amount: line.debit ? parseFloat(line.debit) : undefined,
            credit_amount: line.credit ? parseFloat(line.credit) : undefined,
            job: line.job || undefined,
            department: line.department || undefined,
          })),
      };

      let savedEntry: JournalEntry;
      if (isEdit) {
        savedEntry = await apiAccountingClient.updateJournalEntry(parseInt(id!), formData);
      } else {
        savedEntry = await apiAccountingClient.createJournalEntry(formData);
      }

      // If should post and entry was saved as draft, post it
      if (shouldPost && savedEntry.status === 'DRAFT') {
        await apiAccountingClient.postJournalEntry(savedEntry.id);
      }

      // Navigate back to list
      navigate('/accounting/journal-entries');
    } catch (err) {
      console.error('Failed to save journal entry:', err);
      setError('Failed to save journal entry');
    } finally {
      setSaving(false);
    }
  };

  const getAccountName = (accountId: number | null) => {
    if (!accountId) return '';
    const account = accounts.find(a => a.id === accountId);
    return account ? `${account.account_number} - ${account.name}` : '';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/accounting/journal-entries')}>
          <ArrowBack />
        </IconButton>
        <Typography level="h3" sx={{ fontSize: { xs: '1.25rem', md: undefined } }}>
          {isView ? 'Journal Entry Details' : isEdit ? 'Edit Journal Entry' : 'New Journal Entry'}
        </Typography>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ p: { xs: 1.5, md: 3 } }}>
          {/* Header Information */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid xs={12} sm={6} md={3}>
              <FormControl>
                <FormLabel>Entry Date</FormLabel>
                <Input
                  type="date"
                  value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)}
                  readOnly={isView}
                />
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <FormControl>
                <FormLabel>Entry Type</FormLabel>
                <Select
                  value={entryType}
                  onChange={(_, value) => setEntryType(value as any)}
                  disabled={isView}
                >
                  <Option value="STANDARD">Standard</Option>
                  <Option value="ADJUSTING">Adjusting</Option>
                  <Option value="REVERSING">Reversing</Option>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <FormControl>
                <FormLabel>Reference Number</FormLabel>
                <Input
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="Optional"
                  readOnly={isView}
                />
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '36px' }}>
                  <Chip color="neutral" variant="soft">
                    Draft
                  </Chip>
                </Box>
              </FormControl>
            </Grid>
          </Grid>

          <FormControl sx={{ mb: 3 }}>
            <FormLabel>Memo / Description</FormLabel>
            <Textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="Enter a description for this journal entry"
              minRows={2}
              readOnly={isView}
            />
          </FormControl>

          <Divider sx={{ my: 3 }} />

          {/* Journal Lines */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography level="title-md">Journal Lines</Typography>
            {!isView && (
              <Button
                size="sm"
                startDecorator={<Add />}
                onClick={addLine}
                variant="soft"
              >
                Add Line
              </Button>
            )}
          </Box>

          <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
            <Table sx={{ minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={{ width: '35%' }}>Account</th>
                  <th style={{ width: '25%' }}>Description</th>
                  <th style={{ width: '15%' }}>Debit</th>
                  <th style={{ width: '15%' }}>Credit</th>
                  <th style={{ width: '10%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={index}>
                    <td>
                      <Select
                        value={line.account}
                        onChange={(_, value) => handleLineChange(index, 'account', value)}
                        placeholder="Select account"
                        size="sm"
                        disabled={isView}
                      >
                        {accounts.map(account => (
                          <Option key={account.id} value={account.id}>
                            {account.account_number} - {account.name}
                          </Option>
                        ))}
                      </Select>
                    </td>
                    <td>
                      <Input
                        value={line.description}
                        onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                        placeholder="Optional"
                        size="sm"
                        readOnly={isView}
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        value={line.debit}
                        onChange={(e) => handleLineChange(index, 'debit', e.target.value)}
                        placeholder="0.00"
                        size="sm"
                        disabled={!!line.credit || isView}
                        slotProps={{
                          input: {
                            step: '0.01',
                            min: '0',
                          },
                        }}
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        value={line.credit}
                        onChange={(e) => handleLineChange(index, 'credit', e.target.value)}
                        placeholder="0.00"
                        size="sm"
                        disabled={!!line.debit || isView}
                        slotProps={{
                          input: {
                            step: '0.01',
                            min: '0',
                          },
                        }}
                      />
                    </td>
                    <td>
                      {!isView && (
                        <IconButton
                          size="sm"
                          color="danger"
                          onClick={() => removeLine(index)}
                          disabled={lines.length <= 2}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                    Totals:
                  </td>
                  <td style={{ fontWeight: 'bold' }}>
                    {formatCurrency(totalDebits)}
                  </td>
                  <td style={{ fontWeight: 'bold' }}>
                    {formatCurrency(totalCredits)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </Table>
          </Sheet>

          {/* Balance Status */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            {isBalanced ? (
              <Chip color="success" variant="soft">
                ✓ Balanced
              </Chip>
            ) : (
              <Chip 
                color="danger" 
                variant="soft"
                startDecorator={<Warning />}
              >
                Out of Balance by {formatCurrency(Math.abs(totalDebits - totalCredits))}
              </Chip>
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ mt: 4, display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 2 }, justifyContent: 'flex-end' }}>
            {isView ? (
              <>
                <Button
                  variant="plain"
                  onClick={() => navigate('/accounting/journal-entries')}
                >
                  Back to List
                </Button>
                <Button
                  color="primary"
                  onClick={() => navigate(`/accounting/journal-entries/${id}/edit`)}
                >
                  Edit Entry
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="plain"
                  onClick={() => navigate('/accounting/journal-entries')}
                >
                  Cancel
                </Button>
                <Button
                  startDecorator={<Save />}
                  onClick={() => handleSave(false)}
                  loading={saving}
                  disabled={!isBalanced}
                >
                  Save as Draft
                </Button>
                <Button
                  color="primary"
                  startDecorator={<Send />}
                  onClick={() => handleSave(true)}
                  loading={saving}
                  disabled={!isBalanced}
                >
                  Save & Post
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Card>
    </Box>
  );
};